import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import { Users, Heart, Globe, ArrowRight, MessageSquare, Sparkles } from 'lucide-react';

const DailyLifePage = () => {
  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-babyblue-50 via-babyblue-100/50 to-white" data-testid="daily-life-page">
      <Navbar />

      <section className="py-16 md:py-20 px-6 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-rose-100/40 rounded-full filter blur-3xl -translate-y-1/2 translate-x-1/2"></div>
        <div className="container mx-auto max-w-4xl relative z-10 text-center">
          <div className="inline-flex items-center gap-2 bg-rose-100 text-rose-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Heart className="w-4 h-4" />
            Integration Module
          </div>
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-babyblue-800 mb-4">
            Daily Life &amp; Culture
          </h1>
          <p className="text-xl text-babyblue-600 leading-relaxed max-w-3xl mx-auto">
            How Germans actually think — and how to navigate it like a local.
          </p>
        </div>
      </section>

      {/* Approaching People */}
      <section className="py-12 px-6">
        <div className="container mx-auto max-w-4xl">
          <div className="bg-white rounded-2xl shadow-md border border-babyblue-200 p-6 md:p-8" data-testid="approaching-card">
            <div className="flex items-start gap-4 mb-4">
              <div className="p-3 bg-blue-100 rounded-xl flex-shrink-0">
                <MessageSquare className="w-7 h-7 text-blue-700" />
              </div>
              <div>
                <h2 className="font-heading text-2xl font-bold text-babyblue-800">Approaching People</h2>
                <p className="text-babyblue-500 italic text-sm">First impressions, German-style</p>
              </div>
            </div>
            <p className="text-babyblue-700 leading-relaxed mb-3">
              Germans are <strong>reserved and emotionally measured</strong>. If you approach them, stay calm, be direct, and ask questions clearly.
            </p>
            <p className="text-babyblue-700 leading-relaxed">
              Many newcomers think Germans are rude — they're not. They simply <strong>protect their energy</strong> and want a clear picture of what they're getting into. There's a German word for this: <strong>Vorsicht</strong> (caution / forethought) — we think before we commit.
            </p>
            <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-r-lg mt-5">
              <p className="text-blue-900 text-sm">
                <strong>Practical tip:</strong> Skip the small talk. Get to the point politely, smile once, and don't be offended if the reply is short and factual. That's friendliness, German edition.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Diversity */}
      <section className="py-12 px-6">
        <div className="container mx-auto max-w-4xl">
          <div className="bg-gradient-to-br from-rose-50 to-pink-50 border-2 border-rose-300 rounded-2xl p-6 md:p-8" data-testid="diversity-card">
            <div className="flex items-start gap-4 mb-4">
              <div className="p-3 bg-rose-200 rounded-xl flex-shrink-0">
                <Globe className="w-7 h-7 text-rose-800" />
              </div>
              <div>
                <h2 className="font-heading text-2xl font-bold text-rose-900">Diversity</h2>
                <p className="text-rose-700 italic text-sm">A pluralistic, modern society</p>
              </div>
            </div>
            <p className="text-rose-900 leading-relaxed mb-3">
              Germany is a diverse country: people from many migration backgrounds, religions, members of the LGBTQ+ community, and people with disabilities all share our cities and workplaces.
            </p>
            <p className="text-rose-900 leading-relaxed mb-4">
              Respect for the individual is a core value. Discrimination based on origin, religion, sexual orientation, gender, or disability is illegal under the <em>Allgemeines Gleichbehandlungsgesetz</em> (AGG).
            </p>
            <div className="bg-white rounded-xl p-5 border-2 border-rose-300">
              <p className="text-rose-900 font-bold text-lg flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-rose-600" />
                The golden rule
              </p>
              <p className="text-rose-900 mt-1">Treat people the way you want to be treated.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Related */}
      <section className="py-12 px-6 bg-white">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="font-heading text-xl font-bold text-babyblue-800 mb-6">Continue Your Integration</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {[
              { icon: '🏠', title: 'Finding Housing', path: '/integration/housing' },
              { icon: '🎓', title: 'Education & Training', path: '/integration/education' },
              { icon: '🚗', title: "Driver's License", path: '/integration/fuhrerschein' },
            ].map((item, i) => (
              <Link key={i} to={item.path} className="bg-babyblue-50 rounded-xl border border-babyblue-200 p-4 flex items-center gap-3 hover:border-primary hover:shadow-md transition-all">
                <span className="text-2xl">{item.icon}</span>
                <span className="text-babyblue-700 font-medium">{item.title}</span>
                <ArrowRight className="w-4 h-4 text-babyblue-400 ml-auto" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default DailyLifePage;
